import axios, { AxiosError, AxiosRequestHeaders, AxiosResponse } from 'axios';
// import pako from 'pako';
import { CONFIG } from 'shared/config';
import { ENDPOINTS } from 'shared/constants/ENDPOINTS';
import { IRefreshTokenResponsePayload } from 'shared/interfaces/IUser';
import { authToken } from 'shared/utils/authToken';
import { ls } from 'shared/utils/ls';
import { addBreadcrumb, captureException, captureMessage } from '../../sentry';

export interface IAxios<P, B> {
  url?: string;
  params?: P;
  body?: B;
  data?: B;
  headers?: AxiosRequestHeaders;
  onUploadProgress?: (event: any) => void;
}

interface SentryOptions {
  includeRequestData?: boolean;
  includeResponseData?: boolean;
  filterSensitiveData?: boolean;
  sensitiveKeys?: string[];
  breadcrumbCategory?: string;
  tags?: Record<string, string>;
}

const { removeLS, setLS } = ls();
const { getAuthToken, getRefreshToken } = authToken();

// Filter sensitive data from objects
const filterSensitiveInfo = (
  data: any,
  sensitiveKeys: string[] = ['password', 'token', 'authorization', 'refresh_token', 'auth_token', 'cookie', 'secret'],
  filterEnabled: boolean = true,
): any => {
  if (!filterEnabled || !data || typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    return data.map((item) => filterSensitiveInfo(item, sensitiveKeys, filterEnabled));
  }

  return Object.entries(data).reduce(
    (acc, [key, value]) => {
      // Check if current key should be redacted
      const shouldRedact = sensitiveKeys.some((sensitiveKey) => key.toLowerCase().includes(sensitiveKey.toLowerCase()));

      if (shouldRedact) {
        acc[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        // Recursively filter nested objects
        acc[key] = filterSensitiveInfo(value, sensitiveKeys, filterEnabled);
      } else {
        acc[key] = value;
      }

      return acc;
    },
    {} as Record<string, any>,
  );
};

export const useAxios = (sentryOptions: SentryOptions = {}) => {
  // Default Sentry options
  const {
    includeRequestData = true,
    includeResponseData = true,
    filterSensitiveData = true,
    sensitiveKeys = ['password', 'token', 'authorization', 'refresh_token', 'auth_token', 'cookie', 'secret'],
    breadcrumbCategory = 'axios',
    tags = {},
  } = sentryOptions;

  const bearerToken = getAuthToken();
  const instance = axios.create({
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // // [Sending compressed data] - Add request interceptor to compress data
  // instance.interceptors.request.use((config) => {
  //   if (config.data) {
  //     try {
  //       // Compress the request body using pako
  //       // const compressedData = pako.gzip(JSON.stringify(config.data));

  //       // Optionally encode to Base64 if the API expects a string
  //       const compressedData = btoa(String.fromCharCode(...pako.gzip(JSON.stringify(config.data))));

  //       // Update Axios config
  //       config.data = compressedData;
  //       if (config.headers) {
  //         config.headers['Content-Encoding'] = 'gzip';
  //         config.headers['Content-Type'] = 'application/json';
  //       }
  //     } catch (error) {
  //       console.error('Error compressing request data:', error);
  //     }
  //   }
  //   return config;
  // });

  // // [Receiving compressed data] - Add response interceptor to decompress data
  // instance.interceptors.response.use((response) => {
  //   if (response.headers['content-encoding'] === 'gzip' && response.data) {
  //     try {
  //       // Decompress the response data
  //       // const decompressed = pako.ungzip(response.data, { to: 'string' });
  //       // response.data = JSON.parse(decompressed);

  //       // Optionally decode from Base64 if needed
  //       const decodedData = atob(response.data);
  //       response.data = JSON.parse(
  //         pako.ungzip(
  //           Uint8Array.from(decodedData, (c) => c.charCodeAt(0)),
  //           { to: 'string' },
  //         ),
  //       );
  //     } catch (error) {
  //       console.error('Error decompressing response data:', error);
  //     }
  //   }
  //   return response;
  // });

  instance.interceptors.request.use(
    (request) => {
      const bearerToken = getAuthToken();
      if (request.headers && bearerToken && !request.headers['Authorization']) {
        request.headers['Authorization'] = `Bearer ${bearerToken}`;
      }

      if (CONFIG.isDevelopment) {
        console.log(
          `%c ${request?.method} request for ${request.url}\n`,
          'color:white;background-color:#fa8c16;padding:5px;border-radius:5px;',
          request.data,
        );
        console.log({ params: request.params });
      }

      return request;
    },
    async (error) => {
      if (CONFIG.isDevelopment) console.log(error);

      // Capture request setup errors in Sentry
      captureException(error, {
        tags: {
          source: 'axios.request',
          ...tags,
        },
        extra: {
          error: error.message,
        },
      });

      return Promise.reject(error);
    },
  );

  instance.interceptors.response.use(
    (response) => {
      const bearerToken = getAuthToken();
      if (response.headers && bearerToken) {
        response.headers['Authorization'] = `Bearer ${bearerToken}`;
      }

      if (CONFIG.isDevelopment) {
        console.log(
          `%c response from ${response.config.url}\n`,
          'color:white;background-color:#1890ff;padding:5px;border-radius:5px;',
          response.data,
        );
      }

      return response;
    },
    async (error: AxiosError) => {
      const originalConfig = error.config;
      if (CONFIG.isDevelopment) console.log(error);

      // Add error breadcrumb for Sentry
      const errorData = {
        url: originalConfig?.url,
        method: originalConfig?.method?.toUpperCase(),
        status: error.response?.status,
        statusText: error.response?.statusText,
        ...(includeRequestData &&
          originalConfig && {
            requestData: filterSensitiveInfo(originalConfig.data, sensitiveKeys, filterSensitiveData),
            requestParams: filterSensitiveInfo(originalConfig.params, sensitiveKeys, filterSensitiveData),
          }),
        ...(includeResponseData &&
          error.response && {
            responseData: filterSensitiveInfo(error.response.data, sensitiveKeys, filterSensitiveData),
          }),
      };

      addBreadcrumb({
        category: breadcrumbCategory,
        type: 'http',
        level: 'error',
        data: errorData,
        message: `Axios Error: ${error.response?.status || 'Network Error'} ${error.response?.statusText || error.message}`,
      });

      // Handle token refresh logic
      if (error.response) {
        if (originalConfig?.url !== '/users/signin') {
          if (error.response.status === 401 && !originalConfig._retry) {
            originalConfig._retry = true;
            const bearerToken = getAuthToken();
            try {
              if (getRefreshToken) {
                const response: AxiosResponse<IRefreshTokenResponsePayload> = await axios.post(
                  ENDPOINTS.REFRESH_TOKEN,
                  {
                    refresh_token: getRefreshToken,
                    token: bearerToken,
                  },
                );
                setLS('auth_token', response.data.token);
                setLS('refresh_token', response.data.refresh_token);
              } else {
                if (bearerToken) {
                  removeLS('auth_token');
                  localStorage.clear();
                  window.location.href = '/login';
                }
              }
              return;
            } catch (err) {
              return Promise.reject(err);
            }
          }
        }

        // Capture 4xx and 5xx errors in Sentry
        const status = error.response.status;
        if (status >= 400 && status < 600) {
          if (status >= 400 && status < 500) {
            // For 4xx errors, use captureMessage with warning level
            captureMessage({
              message: `HTTP ${status} ${error.response.statusText}: ${originalConfig?.url}`,
              level: 'warning',
            });
          } else {
            // For 5xx errors, use captureException
            captureException(error, {
              tags: {
                source: 'axios.response',
                httpStatus: status.toString(),
                ...tags,
              },
              extra: errorData,
              contexts: {
                axios: {
                  url: originalConfig?.url,
                  method: originalConfig?.method,
                  status: status,
                  statusText: error.response.statusText,
                },
              },
            });
          }
        }
      } else if (error.request) {
        // Network errors (no response received)
        captureException(error, {
          tags: {
            source: 'axios.network',
            ...tags,
          },
          extra: {
            url: originalConfig?.url,
            method: originalConfig?.method,
          },
        });
      }

      return Promise.reject(error);
    },
  );

  const GET = async <R, P = unknown, B = unknown>(args: IAxios<P, B>): Promise<AxiosResponse<R>> => {
    return await instance({
      ...args,
      method: 'GET',
    });
  };

  const POST = async <R, P = unknown, B = unknown>(args: IAxios<P, B>): Promise<AxiosResponse<R>> => {
    return await instance({
      ...args,
      method: 'POST',
    });
  };

  const PUT = async <P, B = unknown>(args: IAxios<P, B>): Promise<AxiosResponse> => {
    return await instance({
      ...args,
      method: 'PUT',
    });
  };

  const PATCH = async <P, B = unknown>(args: IAxios<P, B>): Promise<AxiosResponse> => {
    return await instance({
      ...args,
      method: 'PATCH',
    });
  };

  const DELETE = async <P, B = unknown>(args: IAxios<P, B>): Promise<AxiosResponse> => {
    return await instance({
      ...args,
      method: 'DELETE',
    });
  };

  return {
    instance,
    GET,
    POST,
    PUT,
    PATCH,
    DELETE,
  };
};
